import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Paper, Typography, TextField, Button, Box, Snackbar, Alert } from '@mui/material';
import { BusinessService } from '../../services/api';

const CampaignCreatorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaignId');

  const [title, setTitle] = useState('');
  const [brand, setBrand] = useState('');
  const [value, setValue] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [minValue, setMinValue] = useState<number | ''>('');
  const [maxValue, setMaxValue] = useState<number | ''>('');

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // Gallery state
  const [images, setImages] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  // temp gallery id used before campaign is created
  const [tempGalleryId] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    if (campaignId) {
      // Fetch campaign details and pre-fill the form
      const fetchCampaign = async () => {
        try {
          const response = await BusinessService.getCampaign(Number(campaignId));
          const campaign = response.data;
          setTitle(campaign.title);
          setBrand(campaign.brand);
          setValue(campaign.value);
          setDescription(campaign.description || '');
          setMinValue(campaign.min_value_per_card || '');
          setMaxValue(campaign.max_value_per_card || '');
        } catch (err) {
          setErrorMessage('Failed to fetch campaign details');
        }
      };

      fetchCampaign();
    }
  }, [campaignId]);

  const validate = () => {
    if (!title.trim()) {
      setErrorMessage('Title is required');
      return false;
    }
    if (!brand.trim()) {
      setErrorMessage('Brand is required');
      return false;
    }
    if (value === '' || Number(value) <= 0) {
      setErrorMessage('Value must be greater than 0');
      return false;
    }
    return true;
  };

  // --- Gallery helpers using legacy api.php endpoints in code_site_srv.ts ---
  const listGallery = (saleId?: number) => {
    const fd = new FormData();
    fd.append('data', JSON.stringify({
      saleId: saleId || 0,
      tempId: saleId ? 0 : tempGalleryId,
    }));
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      try { setImages(JSON.parse(xhr.responseText)); } catch { setImages([]); }
    });
    xhr.open('POST', 'api.php/ListGalleryImages');
    xhr.send(fd);
  };

  const uploadFiles = (files: FileList) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let idx = 0;
    const next = () => {
      if (idx >= files.length) { setUploading(false); listGallery(campaignId ? Number(campaignId) : undefined); return; }
      const f = files[idx++];
      const fd = new FormData();
      fd.append('f', f);
      fd.append('data', JSON.stringify({
        saleId: campaignId ? Number(campaignId) : 0,
        tempId: campaignId ? 0 : tempGalleryId,
      }));
      const xhr = new XMLHttpRequest();
      xhr.addEventListener('load', () => next());
      xhr.open('POST', 'api.php/UploadGalleryImage');
      xhr.send(fd);
    };
    next();
  };

  const deleteImage = (id: number) => {
    const fd = new FormData();
    fd.append('data', JSON.stringify({ id }));
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => listGallery(campaignId ? Number(campaignId) : undefined));
    xhr.open('POST', 'api.php/DeleteGalleryImage');
    xhr.send(fd);
  };

  const attachTempToSale = (saleId: number) => {
    const fd = new FormData();
    fd.append('data', JSON.stringify({ tempId: tempGalleryId, saleId }));
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => listGallery(saleId));
    xhr.open('POST', 'api.php/AttachGalleryTemp');
    xhr.send(fd);
  };


  const handleSave = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    if (!validate()) return;

    const payload: any = {
      title: title.trim(),
      brand: brand.trim(),
      value: Number(value),
    };

    if (description.trim()) payload.description = description.trim();
    if (minValue !== '') payload.min_value_per_card = Number(minValue);
    if (maxValue !== '') payload.max_value_per_card = Number(maxValue);

    try {
      if (campaignId) {
        await BusinessService.updateCampaign(Number(campaignId), payload);
        setSuccessMessage('Campaign updated successfully');
        // refresh images after update
        listGallery(Number(campaignId));
      } else {
        const res = await BusinessService.createCampaign(payload);
        let newId = res?.data?.campaign?.nSaleId || res?.data?.nSaleId;
        if (newId) {
          setSuccessMessage(`Campaign created (ID: ${newId})`);
          // attach temp gallery rows to new sale id
          attachTempToSale(newId);
        } else {
          setSuccessMessage('Campaign created successfully');
        }
      }

      // Clear form
      setTitle('');
      setBrand('');
      setValue('');
      setDescription('');
      setMinValue('');
      setMaxValue('');
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to save campaign';
      setErrorMessage(String(msg));
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  useEffect(() => {
    // initial gallery load
    listGallery(campaignId ? Number(campaignId) : undefined);
  }, [campaignId]);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {campaignId ? 'Update Campaign' : 'Create New Campaign'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <TextField
            label="Brand"
            fullWidth
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
          />

          <TextField
            label="Value"
            type="number"
            fullWidth
            value={value}
            onChange={(e) => {
              const v = e.target.value;
              setValue(v === '' ? '' : Number(v));
            }}
            helperText="Primary value for the campaign (required)"
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Min Value Per Card"
              type="number"
              value={minValue}
              onChange={(e) => {
                const v = e.target.value;
                setMinValue(v === '' ? '' : Number(v));
              }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Max Value Per Card"
              type="number"
              value={maxValue}
              onChange={(e) => {
                const v = e.target.value;
                setMaxValue(v === '' ? '' : Number(v));
              }}
              sx={{ flex: 1 }}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Images</Typography>
            <Button variant="outlined" component="label" sx={{ mr: 2 }} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Add Images'}
              <input hidden type="file" multiple accept="image/*" onChange={(e) => { if (e.target.files) uploadFiles(e.target.files); e.target.value=''; }} />
            </Button>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
              {images.map((img: any) => (
                <Box key={img.nId} sx={{ position: 'relative' }}>
                  <img src={`${img.vImg}`} alt="gallery" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} />
                  <Button size="small" color="error" variant="contained" onClick={() => deleteImage(img.nId)} style={{ position: 'absolute', top: 0, right: 0, minWidth: 0, padding: '2px 6px' }}>X</Button>
                </Box>
              ))}
              {images.length === 0 && <Typography variant="body2" color="text.secondary">No images uploaded yet.</Typography>}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={uploading}>
              {campaignId ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar open={!!errorMessage} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CampaignCreatorPage;
